/**
 * Size finder for swimwear. Suggests a size from the customer's measurements using the
 * Marquise size chart, only among the sizes the product offers and that are in stock for the
 * selected colour (read live from the variant picker). Products that run small get one size up.
 */

/** Marquise size chart, in inches. */
const CHART = {
  XS: { bust: [30, 32], waist: [22, 24], hips: [32, 34], eu: 32, us: 2, uk: 4 },
  S: { bust: [32, 34], waist: [24, 26], hips: [34, 36], eu: 34, us: 4, uk: 6 },
  M: { bust: [36, 38], waist: [28, 30], hips: [38, 40], eu: 36, us: 6, uk: 8 },
  L: { bust: [38, 40], waist: [30, 32], hips: [40, 42], eu: 38, us: 8, uk: 10 },
  XL: { bust: [40, 42], waist: [32, 34], hips: [42, 44], eu: 40, us: 10, uk: 12 },
};
const ORDER = ['XS', 'S', 'M', 'L', 'XL'];
const CM_PER_INCH = 2.54;

class SizeGuide extends HTMLElement {
  unit = 'cm';

  connectedCallback() {
    // Already HTML-escaped by Liquid's `t` filter, so inserted as-is.
    this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
    this.offered = (this.dataset.offered || '').split(',').filter(Boolean);
    this.inputs = [...this.querySelectorAll('input[data-measure]')];
    this.result = this.querySelector('[data-result]');
    this.table = this.querySelector('[data-table]');

    this.inputs.forEach((input) => input.addEventListener('input', this.#update));
    this.querySelectorAll('[data-unit]').forEach((button) => button.addEventListener('click', this.#setUnit));
    this.result?.addEventListener('click', this.#handlePick);
    this.querySelector('.size-guide__open')?.addEventListener('click', this.#update);
    this.#update();
  }

  /** Size option radio for a size in this product's variant picker. */
  #sizeInput(size) {
    const scope = this.closest('.shopify-section, dialog') || document;
    const picker = scope.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    return picker?.querySelector(
      `input[data-option-name="${CSS.escape(this.dataset.sizeOption || '')}"][value="${CSS.escape(size)}"]`
    );
  }

  /** In stock for the currently selected colour. */
  #isAvailable(size) {
    const input = this.#sizeInput(size);
    return input ? input.dataset.optionAvailable === 'true' : true;
  }

  /** @param {number} inches */
  #format(inches) {
    return this.unit === 'cm' ? Math.round(inches * CM_PER_INCH) : inches;
  }

  /** Smallest chart size whose range covers the measurement; between two sizes we go up. */
  #chartIndex(measure, inches) {
    const index = ORDER.findIndex((size) => inches <= CHART[size][measure][1]);
    return index === -1 ? ORDER.length : index;
  }

  #recommend() {
    const values = {};
    for (const input of this.inputs) {
      const value = parseFloat(input.value.replace(',', '.'));
      if (!value || value <= 0) return null;
      values[input.dataset.measure] = this.unit === 'cm' ? value / CM_PER_INCH : value;
    }
    if (!this.inputs.length) return null;

    let index = -1;
    let drivers = [];
    for (const [measure, inches] of Object.entries(values)) {
      const i = this.#chartIndex(measure, inches);
      if (i > index) {
        index = i;
        drivers = [measure];
      } else if (i === index) {
        drivers.push(measure);
      }
    }

    const chartSize = ORDER[index] || null;
    if (this.hasAttribute('data-runs-small')) index += 1;
    return { index, ideal: ORDER[index] || null, chartSize, drivers };
  }

  #update = () => {
    const rec = this.#recommend();
    this.#renderTable(rec?.chartSize);
    if (!this.result) return;
    if (!rec) {
      this.result.hidden = true;
      return;
    }

    const s = this.strings;
    const fill = (text, key, value) => (text || '').replace(key, value);
    const kicker = `<p class="size-guide__kicker">${(s.suggested)}</p>`;
    let html;
    let warning = true;

    if (rec.ideal && this.offered.includes(rec.ideal) && this.#isAvailable(rec.ideal)) {
      const measures = rec.drivers.map((m) => s[m].toLowerCase()).join(` ${s.and} `);
      const why =
        this.hasAttribute('data-runs-small') && rec.chartSize
          ? fill(s.runs_small_result, '__SIZE__', rec.chartSize)
          : fill(s.based_on, '__MEASURES__', measures);
      html = `${kicker}<p class="size-guide__size">${rec.ideal}</p><p>${(why)}</p>
        <button type="button" class="button size-guide__pick" data-pick="${rec.ideal}">
          ${(fill(s.pick, '__SIZE__', rec.ideal))}
        </button>`;
      warning = false;
    } else if (rec.ideal && this.offered.includes(rec.ideal)) {
      const available = this.offered.filter((size) => this.#isAvailable(size));
      html = `${kicker}<p class="size-guide__size">${(fill(s.sold_out_title, '__SIZE__', rec.ideal))}</p>
        <p>${(s.sold_out)}</p>
        <p class="size-guide__muted">${(
          fill(s.available_sizes, '__SIZES__', available.join(', ') || s.none)
        )}</p>`;
    } else if (rec.index < ORDER.indexOf(this.offered[0])) {
      html = `${kicker}<p class="size-guide__size">${(s.too_small_title)}</p>
        <p>${(fill(s.too_small, '__SIZE__', this.offered[0]))}</p>
        <p class="size-guide__muted">${s.contact_html}</p>`;
    } else {
      html = `${kicker}<p class="size-guide__size">${(rec.ideal || s.too_large_size)}</p>
        <p>${(s.too_large)}</p>
        <p>${(s.too_large_brand)}</p>
        <p class="size-guide__muted">${s.contact_notify_html}</p>`;
    }

    this.result.innerHTML = html;
    this.result.classList.toggle('is-warning', warning);
    this.result.hidden = false;
  };

  /** @param {string | null | undefined} match */
  #renderTable(match) {
    if (!this.table) return;
    const s = this.strings;
    const unitLabel = this.unit === 'cm' ? 'cm' : s.inches_short;
    const cls = (size) => (size === match ? ' class="is-match"' : '');
    const head = this.offered
      .map((size) => {
        const soldOut = this.#isAvailable(size) ? '' : `<small>${(s.sold_out_short)}</small>`;
        return `<th${cls(size)}>${size}${soldOut}</th>`;
      })
      .join('');

    let rows = '';
    for (const measure of ['bust', 'waist', 'hips']) {
      const cells = this.offered
        .map((size) => {
          const [min, max] = CHART[size][measure];
          return `<td${cls(size)}>${this.#format(min)}-${this.#format(max)}</td>`;
        })
        .join('');
      rows += `<tr><td>${(s[measure])} (${(unitLabel)})</td>${cells}</tr>`;
    }
    for (const [key, label] of [['eu', 'EU'], ['us', 'US'], ['uk', 'UK / AU']]) {
      rows += `<tr><td>${label}</td>${this.offered.map((size) => `<td${cls(size)}>${CHART[size][key]}</td>`).join('')}</tr>`;
    }

    this.table.innerHTML = `<thead><tr><th></th>${head}</tr></thead><tbody>${rows}</tbody>`;
  }

  /** @param {Event} event */
  #setUnit = (event) => {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const unit = button.dataset.unit;
    if (!unit || unit === this.unit) return;

    for (const input of this.inputs) {
      const value = parseFloat(input.value.replace(',', '.'));
      if (value) {
        input.value = String(unit === 'cm' ? Math.round(value * CM_PER_INCH) : Math.round((value / CM_PER_INCH) * 2) / 2);
      }
    }
    this.unit = unit;
    this.querySelectorAll('[data-unit]').forEach((b) => {
      const active = b === button;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-pressed', String(active));
    });
    this.querySelectorAll('[data-unit-label]').forEach((label) => {
      label.textContent = unit === 'cm' ? 'cm' : this.strings.inches_short;
    });
    this.#update();
  };

  /** @param {Event} event */
  #handlePick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-pick]') : null;
    if (!(button instanceof HTMLElement) || !button.dataset.pick) return;

    const input = this.#sizeInput(button.dataset.pick);
    if (input && !input.checked) input.click();
    /** @type {any} */ (this.querySelector('dialog-component'))?.closeDialog?.();
  };
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuide);
}
